import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/client";
import type { AronCostRow, AronCostsMeta } from "../types";

export interface AronCostsResult {
  rows: AronCostRow[];
  meta: AronCostsMeta;
}

const COSTS_KEY = ["aron", "ceo", "expenses"] as const;

// CEO-only spend dashboard. The envelope carries grand totals + budgets in `meta`,
// so we surface both the per-user rows and the meta to the page.
export function useAronCosts() {
  return useQuery<AronCostsResult>({
    queryKey: COSTS_KEY,
    queryFn: async () => {
      const r = await apiClient.get<{ data: AronCostRow[]; meta: AronCostsMeta }>(
        "/aron/ceo/expenses/",
      );
      return { rows: r.data.data, meta: r.data.meta };
    },
  });
}

export interface AiSpendRow {
  key: string;
  label: string;
  cost_usd: string;
  runs: number;
}

export interface AiSpendReport {
  rows: AiSpendRow[];
  total_usd: string;
  chat_usd: string;
  /** Wydatek AI POZA czatem ARON — liczba, której dotąd nie było widać. */
  outside_chat_usd: string;
  sources: number;
}

// Rachunek za AI całej firmy: 13 pól kosztu w 11 modelach 6 aplikacji.
// Strona wydatków ARON pokazuje sam czat, więc bez tego widać ułamek kwoty.
export function useCompanyAiSpend() {
  return useQuery<AiSpendReport>({
    queryKey: ["aron", "ceo", "ai-spend"],
    queryFn: async () => {
      const r = await apiClient.get<{ data: AiSpendReport }>("/aron/ceo/ai-spend/");
      return r.data.data;
    },
  });
}

function useInvalidateCosts() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: COSTS_KEY });
}

// CEO edits the GLOBAL default daily COST cap per user (USD).
export function useUpdateGlobalBudget() {
  const invalidate = useInvalidateCosts();
  return useMutation({
    mutationFn: async (daily_cost_budget_usd: string) => {
      await apiClient.patch("/aron/ceo/daily-budget/", { daily_cost_budget_usd });
    },
    onSuccess: invalidate,
  });
}

// CEO sets a per-user override of the daily COST cap (USD).
export function useSetUserBudget() {
  const invalidate = useInvalidateCosts();
  return useMutation({
    mutationFn: async ({
      userId,
      daily_cost_budget_usd,
    }: {
      userId: string;
      daily_cost_budget_usd: string;
    }) => {
      await apiClient.put(`/aron/ceo/user-budget/${userId}/`, { daily_cost_budget_usd });
    },
    onSuccess: invalidate,
  });
}

// CEO clears a per-user override → the user falls back to the global default.
export function useClearUserBudget() {
  const invalidate = useInvalidateCosts();
  return useMutation({
    mutationFn: async (userId: string) => {
      await apiClient.delete(`/aron/ceo/user-budget/${userId}/`);
    },
    onSuccess: invalidate,
  });
}
